import type { ReactNode } from 'react';
import classNames from 'classnames';

import Info from '@/assets/icons/info.svg?component';
import WarningCircle from '@/assets/icons/warning_circle.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import styles from './EmptyState.module.scss';

export type EmptyStateVariant = 'noData' | 'error';

export interface EmptyStateProps {
    title: ReactNode;
    /** `noData` when there is nothing to show; `error` when loading it failed. */
    variant?: EmptyStateVariant;
    description?: ReactNode;
    /** A next step, such as a retry button. */
    action?: ReactNode;
    /** Replaces the variant's icon. */
    icon?: ReactNode;
    /**
     * `quiet` is a single grey line without an icon, for a chart, table or list that is simply
     * empty; `default` adds the icon and a full-colour title; `prominent` sets a larger title
     * and description, for a good-news state such as "all tasks done".
     */
    appearance?: 'default' | 'quiet' | 'prominent';
    /** `spacious` holds the height of the content it stands in for. */
    size?: 'compact' | 'spacious';
    /** The exact height of the plot or block it replaces, so swapping the two does not jump. */
    height?: number | string;
    /**
     * Announcement when it appears. Defaults: an error interrupts (`role="alert"`), an
     * empty result is not announced.
     */
    live?: 'assertive' | 'polite' | 'off';
    /**
     * Renders the title as this heading, for a state that stands in for a whole page (`h1`)
     * or a titled section. Left out, the title is a paragraph and stays out of the outline.
     */
    headingLevel?: 'h1' | 'h2' | 'h3';
    className?: string;
}

type EmptyStateLive = NonNullable<EmptyStateProps['live']>;

const ROLE: Record<EmptyStateLive, 'alert' | 'status' | undefined> = {
    assertive: 'alert',
    polite: 'status',
    off: undefined,
};

const DEFAULT_ICON: Record<EmptyStateVariant, ReactNode> = {
    noData: <SvgIcon icon={Info} size="medium" color="currentColor" />,
    error: <SvgIcon icon={WarningCircle} size="medium" color="currentColor" />,
};

export function EmptyState(props: EmptyStateProps) {
    const {
        title,
        variant = 'noData',
        description,
        action,
        icon,
        appearance = 'default',
        size = 'compact',
        height,
        live = variant === 'error' ? 'assertive' : 'off',
        headingLevel,
        className,
    } = props;
    const TitleTag = headingLevel ?? 'p';

    return (
        <div
            role={ROLE[live]}
            data-variant={variant}
            style={height === undefined ? undefined : { height }}
            className={classNames(
                styles.emptyState,
                styles[variant],
                styles[size],
                appearance !== 'default' && styles[appearance],
                className
            )}>
            {appearance !== 'quiet' && (
                <span className={styles.icon} aria-hidden="true">
                    {icon ?? DEFAULT_ICON[variant]}
                </span>
            )}
            <TitleTag className={styles.title}>{title}</TitleTag>
            {description != null && <div className={styles.description}>{description}</div>}
            {action != null && <div className={styles.action}>{action}</div>}
        </div>
    );
}

export default EmptyState;
